(function () {
  const root = document.getElementById("swatch-app");
  if (!root) return;

  const state = {
    data: null,
    allSwatches: [],
    filteredSwatches: [],
    selected: new Map(),
    cartSwatchIds: new Set(),
    cartSwatchLineMap: new Map(),
    removingSwatchIds: new Set(),
    activeTier: "all",
    search: "",
    visibleCount: 24,
    previewSwatchId: null,
    modalSwatchId: null,
    isMobileModalOpen: false,
    cartItemCount: 0,
    isAdding: false,
  };

  const config = {
    productId: root.dataset.productId,
    productTitle: root.dataset.productTitle || "",
    productHandle: root.dataset.productHandle || "",
    variantId: Number(root.dataset.variantId),
    jsonFile: root.dataset.jsonFile || "",
    moneyFormat: root.dataset.moneyFormat || "${{amount}}",
  };

  function escapeHtml(str) {
    return String(str || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  let cartSyncTimer = null;
  let cartDrawerObserver = null;

  function scheduleCartSync(delay = 250) {
    if (cartSyncTimer) clearTimeout(cartSyncTimer);

    cartSyncTimer = setTimeout(async () => {
      await syncSwatchAppFromCart();
    }, delay);
  }

  function buildBcpoPreviewUrl(url, width = 600) {
    try {
      const parts = url.split("/");
      const payload = parts.pop();
      const decoded = JSON.parse(atob(payload));

      if (decoded.edits && decoded.edits.resize) {
        decoded.edits.resize.width = width;
      }

      const rebuilt = btoa(JSON.stringify(decoded));
      return [...parts, rebuilt].join("/");
    } catch (e) {
      return url;
    }
  }

  function shouldPauseStickyUpdates() {
    return isCartDrawerOpen();
  }

  let cartDrawerVisibilityObserver = null;

  function setupCartDrawerVisibilityObserver() {
    const drawer = document.querySelector("header-drawer.site-header__drawer.site-header__cart");
    if (!drawer) return;

    if (cartDrawerVisibilityObserver) {
      cartDrawerVisibilityObserver.disconnect();
    }

    cartDrawerVisibilityObserver = new MutationObserver(() => {
      renderMobileCartButton();

      setTimeout(() => {
        updateScrollTopVisibility();
        requestDesktopStickyUpdate();
      }, 180);
    });

    cartDrawerVisibilityObserver.observe(drawer, {
      attributes: true,
      attributeFilter: ["class"]
    });
  }

  let desktopStickyBound = false;
  let desktopStickyTicking = false;

  function getStickyElements() {
    return {
      layout: document.querySelector(".swatch-app__layout"),
      previewWrap: document.querySelector(".swatch-app__preview-wrap"),
      previewCard: document.querySelector(".swatch-preview"),
      selectedWrap: document.querySelector(".swatch-app__selected-wrap"),
      selectedCard: document.querySelector(".swatch-selected")
    };
  }

  function clearStickyCard(card) {
    if (!card) return;
    card.style.position = "";
    card.style.top = "";
    card.style.left = "";
    card.style.width = "";
    card.style.bottom = "";
  }

  function applyStickyCard(layout, wrap, card, enabled) {
    if (!layout || !wrap || !card || !enabled) {
      clearStickyCard(card);
      return;
    }

    const topOffset = 96;
    const scrollY = window.scrollY || window.pageYOffset;

    const layoutRect = layout.getBoundingClientRect();
    const wrapRect = wrap.getBoundingClientRect();

    const layoutTop = layoutRect.top + scrollY;
    const layoutBottom = layoutTop + layout.offsetHeight;
    const cardHeight = card.offsetHeight;

    const start = layoutTop - topOffset;
    const end = layoutBottom - cardHeight - topOffset;

    if (scrollY < start) {
      clearStickyCard(card);
      return;
    }

    if (scrollY <= end) {
      card.style.position = "fixed";
      card.style.top = `${topOffset}px`;
      card.style.left = `${wrapRect.left}px`;
      card.style.width = `${wrapRect.width}px`;
      card.style.bottom = "";
      return;
    }

    card.style.position = "absolute";
    card.style.top = "";
    card.style.left = "0";
    card.style.width = `${wrapRect.width}px`;
    card.style.bottom = "0";
  }

  function updateDesktopStickyFallback() {
    const isTouch = window.matchMedia("(hover: none), (pointer: coarse)").matches;
    const width = window.innerWidth;

    const { layout, previewWrap, previewCard, selectedWrap, selectedCard } = getStickyElements();

    if (isTouch || width < 768) {
      clearStickyCard(previewCard);
      clearStickyCard(selectedCard);
      return;
    }

    applyStickyCard(layout, previewWrap, previewCard, true);
    applyStickyCard(layout, selectedWrap, selectedCard, width >= 1200);
  }

  function requestDesktopStickyUpdate() {
    if (desktopStickyTicking) return;
    if (shouldPauseStickyUpdates()) return;

    desktopStickyTicking = true;

    requestAnimationFrame(() => {
      updateDesktopStickyFallback();
      desktopStickyTicking = false;
    });
  }

  function setupDesktopStickyFallback() {
    if (!desktopStickyBound) {
      window.addEventListener("scroll", requestDesktopStickyUpdate, { passive: true });
      window.addEventListener("resize", requestDesktopStickyUpdate);
      desktopStickyBound = true;
    }

    requestDesktopStickyUpdate();
  }

  function getModalSwatch() {
    if (!state.modalSwatchId) return null;
    return state.allSwatches.find((s) => s.id === state.modalSwatchId) || null;
  }

  function openSwatchModal(id) {
    state.modalSwatchId = id;
    state.isMobileModalOpen = true;
    renderModal();
    document.body.classList.add("swatch-modal-open");
  }

  function closeSwatchModal() {
    state.modalSwatchId = null;
    state.isMobileModalOpen = false;
    renderModal();
    document.body.classList.remove("swatch-modal-open");
  }

  function isCartDrawerOpen() {
    const drawer = document.querySelector("header-drawer.site-header__drawer.site-header__cart");
    if (!drawer) return false;

    return drawer.classList.contains("is-active");
  }

  function renderTierGuide() {
    const guide = Array.isArray(state.data?.tier_guide)
      ? state.data.tier_guide
      : [];
    if (!guide.length) return "";

    return `
    <div class="swatch-tier-guide">
      <strong>Tier Guide:</strong>
      ${guide
        .map((item) => `${escapeHtml(item.tier)} = ${escapeHtml(item.label)}`)
        .join(" • ")}
    </div>
  `;
  }

  function updateScrollTopVisibility() {
    const button = root.querySelector("[data-swatch-scroll-top]");
    if (!button) return;

    if (window.scrollY > 500) {
      button.classList.add("is-visible");
    } else {
      button.classList.remove("is-visible");
    }
  }

  function scrollToSwatchTop() {
    const target =
      root.querySelector(".swatch-app__controls-wrap") ||
      root;

    if (!target) return;

    const top = target.getBoundingClientRect().top + window.scrollY - 90;
    window.scrollTo({ top, behavior: "smooth" });
  }

  function scrollToSelectedPanel() {
    const target = root.querySelector(".swatch-app__selected-wrap");
    if (!target) return;

    const top = target.getBoundingClientRect().top + window.scrollY - 20;
    window.scrollTo({ top, behavior: "smooth" });
  }

  function ensureToastContainer() {
    let container = document.querySelector(".swatch-toast-container");
    if (!container) {
      container = document.createElement("div");
      container.className = "swatch-toast-container";
      document.body.appendChild(container);
    }
    return container;
  }

  function showToast(message, type = "success", action = null) {
    const container = ensureToastContainer();
    const toast = document.createElement("div");
    toast.className = `swatch-toast swatch-toast--${type}`;

    toast.innerHTML = `
    <div class="swatch-toast__content">
      <div class="swatch-toast__message">${escapeHtml(message)}</div>
      ${action
        ? `<a class="swatch-toast__action" href="${escapeHtml(action.href)}">${escapeHtml(action.label)}</a>`
        : ""
      }
    </div>
  `;

    container.appendChild(toast);

    requestAnimationFrame(() => {
      toast.classList.add("is-visible");
    });

    setTimeout(() => {
      toast.classList.remove("is-visible");
      setTimeout(() => {
        if (toast.parentNode) toast.parentNode.removeChild(toast);
      }, 250);
    }, 3000);
  }

  async function syncSwatchAppFromCart() {
    try {
      const cart = await loadCartSwatchIds();

      renderPreview();
      renderGrid();
      renderSelected();
      requestDesktopStickyUpdate();
      renderMobileSelectedBar();
      renderMobileCartButton();
      updateDrawerSwatchRule(cart);

      return cart;
    } catch (error) {
      console.error("Failed to sync swatch app from cart", error);
      return null;
    }
  }

  function setupCartDrawerObserver() {
    if (cartDrawerObserver) {
      cartDrawerObserver.disconnect();
      cartDrawerObserver = null;
    }

    const productsContainer =
      document.querySelector("header-cart-drawer [data-products]") ||
      document.querySelector("header-cart-drawer .cart__scrollable");

    if (!productsContainer) return;

    cartDrawerObserver = new MutationObserver(() => {
      scheduleCartSync(250);
    });

    cartDrawerObserver.observe(productsContainer, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });
  }

  function updateDrawerSwatchRule(cart) {
    const drawer = document.querySelector("header-cart-drawer.cart-drawer");
    if (!drawer) return;

    const warning = drawer.querySelector(".cart__swatch-warning");
    const checkoutButton = drawer.querySelector(".cart__checkout");

    if (warning) {
      warning.remove();
    }

    if (checkoutButton) {
      checkoutButton.removeAttribute("disabled");
      checkoutButton.removeAttribute("aria-disabled");
    }
  }

  async function removeSwatchFromCart(swatchId) {
    const line = state.cartSwatchLineMap.get(swatchId);
    if (!line || state.removingSwatchIds.has(swatchId)) return;

    const swatch = state.allSwatches.find((s) => s.id === swatchId);

    try {
      state.removingSwatchIds.add(swatchId);
      renderGrid();

      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          id: line.key,
          quantity: 0,
          sections: ["header"],
          sections_url: window.location.pathname,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result.description || "Failed to remove swatch from cart.",
        );
      }

      const cart = await loadCartSwatchIds();

      const headerHtml =
        result.sections && result.sections.header ? result.sections.header : "";

      updateCartUIFromSectionHtml(headerHtml, cart);

      state.removingSwatchIds.delete(swatchId);
      renderPreview();
      renderGrid();
      renderSelected();
      requestDesktopStickyUpdate();
      renderMobileSelectedBar();
      renderMobileCartButton();

      showToast(
        swatch
          ? `${swatch.web_name} removed from cart.`
          : "Swatch removed from cart.",
        "success",
      );
    } catch (error) {
      console.error(error);
      state.removingSwatchIds.delete(swatchId);
      renderGrid();
      showToast("There was a problem removing this swatch.", "error");
    }
  }

  async function loadCartSwatchIds() {
    try {
      const response = await fetch("/cart.js", {
        headers: { Accept: "application/json" },
      });
      const cart = await response.json();

      state.cartItemCount = Number(cart.item_count || 0);

      state.cartSwatchIds = new Set();
      state.cartSwatchLineMap = new Map();

      (cart.items || []).forEach((item) => {
        const swatchId = item.properties && item.properties._swatch_id;
        if (!swatchId) return;

        state.cartSwatchIds.add(swatchId);
        state.cartSwatchLineMap.set(swatchId, {
          key: item.key,
          quantity: item.quantity,
        });
      });

      return cart;
    } catch (error) {
      console.error("Failed to load cart swatch ids", error);
      state.cartSwatchIds = new Set();
      state.cartSwatchLineMap = new Map();
      return null;
    }
  }

  function updateCartUIFromSectionHtml(headerHtml, cart) {
    if (!headerHtml) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(headerHtml, "text/html");

    const currentBubble = document.querySelector(".cart-link__bubble");
    const newBubble = doc.querySelector(".cart-link__bubble");
    if (currentBubble && newBubble) {
      currentBubble.outerHTML = newBubble.outerHTML;
    }

    const currentDrawer = document.querySelector(
      "header-cart-drawer.cart-drawer",
    );
    const newDrawer = doc.querySelector("header-cart-drawer.cart-drawer");
    if (currentDrawer && newDrawer) {
      currentDrawer.outerHTML = newDrawer.outerHTML;
    }

    const currentHeaderDrawer = document.querySelector(
      "header-drawer.site-header__drawer.site-header__cart",
    );
    const emptyState =
      currentHeaderDrawer &&
      currentHeaderDrawer.querySelector(".site-header__cart-empty");
    const form =
      currentHeaderDrawer &&
      currentHeaderDrawer.querySelector(".cart__drawer-form");

    if (emptyState && form) {
      if (cart && cart.item_count > 0) {
        form.style.display = "";
        emptyState.style.display = "none";
      } else {
        form.style.display = "none";
        emptyState.style.display = "";
      }
    }

    document.dispatchEvent(
      new CustomEvent("cart:refresh", { detail: { cart } }),
    );
    document.dispatchEvent(
      new CustomEvent("cart:updated", { detail: { cart } }),
    );
    document.dispatchEvent(
      new CustomEvent("theme:cart:changed", { detail: { cart } }),
    );
  }

  async function refreshCartUI() {
    try {
      const cartRes = await fetch("/cart.js", {
        headers: { Accept: "application/json" },
      });
      const cart = await cartRes.json();

      const sectionRes = await fetch(`/?sections=header`);
      const sections = await sectionRes.json();
      const headerHtml = sections && sections.header ? sections.header : "";

      updateCartUIFromSectionHtml(headerHtml, cart);
      return cart;
    } catch (error) {
      console.error("Cart UI refresh failed", error);
      return null;
    }
  }

  function formatMoney(value) {
    const number = Number(value || 0);
    return `$${number.toFixed(2)}`;
  }

  function getSelectedArray() {
    return Array.from(state.selected.values());
  }

  function getPreviewSwatch() {
    if (!state.allSwatches.length) return null;

    if (state.previewSwatchId) {
      const match = state.allSwatches.find(
        (s) => s.id === state.previewSwatchId,
      );
      if (match) return match;
    }

    if (state.filteredSwatches.length) return state.filteredSwatches[0];
    return state.allSwatches[0];
  }

  function extractTierNumber(value) {
    const match = String(value || "").match(/\d+/);
    return match ? Number(match[0]) : 999;
  }

  function buildTierList() {
    if (state.data?.tiers?.length) {
      return state.data.tiers;
    }

    const unique = Array.from(
      new Set(state.allSwatches.map((s) => s.tier).filter(Boolean)),
    );

    unique.sort((a, b) => extractTierNumber(a) - extractTierNumber(b));

    return [
      { value: "all", label: "All Fabrics" },
      ...unique.map((tier) => ({ value: tier, label: tier })),
    ];
  }

  function buildTierCounts() {
    const counts = { all: state.allSwatches.length };
    state.allSwatches.forEach((swatch) => {
      counts[swatch.tier] = (counts[swatch.tier] || 0) + 1;
    });
    return counts;
  }

  function applyFilters() {
    const query = state.search.trim().toLowerCase();
    const normalizedQuery = query.replace(/[\s-]+/g, "");

    state.filteredSwatches = state.allSwatches.filter((swatch) => {
      const tierMatch =
        state.activeTier === "all" || swatch.tier === state.activeTier;

      const searchMatch =
        !query ||
        swatch._searchBlob.includes(query) ||
        swatch._code.includes(query) ||
        swatch._codeNormalized.includes(normalizedQuery) ||
        swatch._codeSuffix.includes(query);

      return tierMatch && searchMatch;
    });

    const previewStillVisible = state.filteredSwatches.some(
      (s) => s.id === state.previewSwatchId,
    );
    if (!previewStillVisible && state.filteredSwatches.length) {
      state.previewSwatchId = state.filteredSwatches[0].id;
    }
  }

  function toggleSwatch(id) {
    if (state.cartSwatchIds.has(id)) {
      return;
    }

    const swatch = state.allSwatches.find((s) => s.id === id);
    if (!swatch) return;

    if (state.selected.has(id)) {
      state.selected.delete(id);
    } else {
      state.selected.set(id, swatch);
    }

    state.previewSwatchId = id;
    renderPreview();
    renderGrid();
    renderSelected();
    requestDesktopStickyUpdate();
    renderModal();
    renderMobileSelectedBar();
    renderMobileCartButton();
  }

  function removeSelected(id) {
    state.selected.delete(id);
    renderPreview();
    renderGrid();
    renderSelected();
    requestDesktopStickyUpdate();
    renderModal();
    renderMobileSelectedBar();
    renderMobileCartButton();
  }

  function setPreview(id) {
    if (!id || state.previewSwatchId === id) return;
    state.previewSwatchId = id;
    renderPreview();
    renderGrid();
    requestDesktopStickyUpdate();
  }

  function loadMore() {
    state.visibleCount += 24;
    renderGrid();
  }

  function resetVisibleCount() {
    state.visibleCount = 24;
  }

  async function addSelectedToCart() {
    const selected = getSelectedArray();
    if (!selected.length || !config.variantId || state.isAdding) return;

    state.isAdding = true;
    renderSelected();
    requestDesktopStickyUpdate();

    const items = selected.map((swatch) => ({
      id: config.variantId,
      quantity: 1,
      properties: {
        _swatch_product_key: swatch.swatch_product_key,
        _swatch_id: swatch.id,
        "Fabric Code": swatch.fabric_code,
        "Fabric Name": swatch.web_name,
        Tier: swatch.tier,
        Collection: swatch.collection_label || "",
        "Openness": swatch.openness || "",
        "_swatch_image": swatch.image_url,
      },
    }));

    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          items,
          sections: ["header"],
          sections_url: window.location.pathname,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result.description || "Failed to add swatches to cart.",
        );
      }

      selected.forEach((swatch) => state.cartSwatchIds.add(swatch.id));
      const cart = await loadCartSwatchIds();

      const headerHtml =
        result.sections && result.sections.header ? result.sections.header : "";

      updateCartUIFromSectionHtml(headerHtml, cart);

      const addedCount = selected.length;
      state.selected.clear();

      renderPreview();
      renderGrid();
      renderSelected();
      requestDesktopStickyUpdate();
      renderMobileSelectedBar();
      renderMobileCartButton();

      showToast(
        `${addedCount} swatch${addedCount > 1 ? "es" : ""} added to cart.`,
        "success",
        { label: "View Cart", href: "/cart" }
      );
    } catch (error) {
      console.error(error);
      showToast("There was a problem adding swatches to cart.", "error");
      renderSelected();
    } finally {
      state.isAdding = false;
      renderSelected();
      requestDesktopStickyUpdate();
    }
  }

  function renderModal() {
    const container = root.querySelector(".swatch-app__modal-wrap");
    if (!container) return;

    const swatch = getModalSwatch();

    if (!state.isMobileModalOpen || !swatch) {
      container.innerHTML = "";
      return;
    }

    container.innerHTML = `
    <div class="swatch-modal is-open" data-swatch-modal>
      <div class="swatch-modal__backdrop" data-swatch-modal-close></div>
      <div class="swatch-modal__dialog" role="dialog" aria-modal="true" aria-label="Swatch preview">
        <button type="button" class="swatch-modal__close" data-swatch-modal-close aria-label="Close preview">
          ×
        </button>

        <div class="swatch-modal__image-wrap">
          <img
            class="swatch-modal__image"
            src="${escapeHtml(buildBcpoPreviewUrl ? buildBcpoPreviewUrl(swatch.image_url, 900) : swatch.image_url)}"
            alt="${escapeHtml(swatch.web_name)}"
          >
        </div>

        <div class="swatch-modal__content">
          <div class="swatch-modal__code">${escapeHtml(swatch.fabric_code)}</div>
          <h3 class="swatch-modal__name">${escapeHtml(swatch.web_name)}</h3>

          ${swatch.openness
        ? `<div class="swatch-modal__badge">${escapeHtml(swatch.openness)} openness</div>`
        : ""
      }

          <div class="swatch-modal__collection">
            ${escapeHtml(swatch.collection_label || swatch.tier || "")}
          </div>
        </div>
      </div>
    </div>
  `;
  }

  function renderMobileSelectedBar() {
    const container = root.querySelector(".swatch-app__mobile-selected-bar-wrap");
    if (!container) return;

    const selected = getSelectedArray();
    const total = selected.length;
    const totalPrice = selected.reduce((sum, item) => sum + Number(item.price || 2), 0);

    if (!total) {
      container.innerHTML = "";
      return;
    }

    container.innerHTML = `
    <div class="swatch-mobile-selected-bar is-visible">
      <div class="swatch-mobile-selected-bar__info">
        <strong>${total}</strong> selected • <strong>${formatMoney(totalPrice)}</strong>
      </div>
      <button
        type="button"
        class="swatch-mobile-selected-bar__button"
        data-swatch-review-selected
      >
        Review Selected
      </button>
    </div>
  `;
  }

  function renderMobileCartButton() {
    const container = root.querySelector(".swatch-app__mobile-cart-button-wrap");
    if (!container) return;

    if (!state.cartItemCount || isCartDrawerOpen()) {
      container.innerHTML = "";
      return;
    }

    container.innerHTML = `
    <button type="button" class="swatch-mobile-cart-button" data-swatch-open-cart>
      View Cart (${state.cartItemCount})
    </button>
  `;
  }

  function renderShell() {
    root.innerHTML = `
    <div class="swatch-app__header-wrap"></div>
    <div class="swatch-app__layout">
      <div class="swatch-app__preview-wrap"></div>
      <div class="swatch-browser">
        <div class="swatch-app__controls-wrap"></div>
        <div class="swatch-app__grid-wrap"></div>
      </div>
      <div class="swatch-app__selected-wrap"></div>
    </div>
    <button type="button" class="swatch-scroll-top" data-swatch-scroll-top aria-label="Back to top">
      ↑
    </button>
    <div class="swatch-app__mobile-selected-bar-wrap"></div>
    <div class="swatch-app__mobile-cart-button-wrap"></div>
    <div class="swatch-app__modal-wrap"></div>
  `;
  }

  function renderHeader() {
    const container = root.querySelector(".swatch-app__header-wrap");
    const price = state.data?.swatch_price != null ? formatMoney(state.data.swatch_price) : "$2.00";

    container.innerHTML = `
    <div class="swatch-app__header">
      <h1 class="swatch-app__title">${escapeHtml(state.data?.product_label || config.productTitle)}</h1>
      <p class="swatch-app__intro">
        Compare fabric colors and textures at home before ordering your shades.
      </p>

      <div class="swatch-app__info-grid">
        <div class="swatch-app__info-card">
          <div class="swatch-app__info-label">Price</div>
          <div class="swatch-app__info-value">${price} each</div>
        </div>

        <div class="swatch-app__info-card">
          <div class="swatch-app__info-label">Shipping</div>
          <div class="swatch-app__info-value">Flat $5 on all swatch orders</div>
        </div>

        <div class="swatch-app__info-card">
          <div class="swatch-app__info-label">Mix & Match</div>
          <div class="swatch-app__info-value">Across collections and swatch pages</div>
        </div>

        <div class="swatch-app__info-card">
          <div class="swatch-app__info-label">Delivery</div>
          <div class="swatch-app__info-value">Ships within 1 week</div>
        </div>
      </div>
    </div>
  `;
  }


  function renderPreview() {
    const container = root.querySelector(".swatch-app__preview-wrap");
    const swatch = getPreviewSwatch();

    if (!swatch) {
      container.innerHTML = `<aside class="swatch-preview"><div class="swatch-app__empty">No swatch available.</div></aside>`;
      return;
    }

    container.innerHTML = `
      <aside class="swatch-preview">
        <div class="swatch-preview__image-wrap">
          <img class="swatch-preview__image" src="${escapeHtml(buildBcpoPreviewUrl(swatch.image_url, 600))}" alt="${escapeHtml(swatch.web_name)}">
        </div>
        <div class="swatch-preview__code">${escapeHtml(swatch.fabric_code)}</div>
        <h2 class="swatch-preview__name">${escapeHtml(swatch.web_name)}</h2>
        <div class="swatch-preview__meta">
          ${escapeHtml(swatch.collection_label || swatch.tier || "")}
          ${state.selected.has(swatch.id) ? " • Selected" : ""}
        </div>
        ${swatch.openness
        ? `<div class="swatch-preview__openness-badge">${escapeHtml(swatch.openness)} openness</div>`
        : ""
      }
      </aside>
    `;
  }

  function renderControls() {
    const container = root.querySelector(".swatch-app__controls-wrap");
    const tierCounts = buildTierCounts();
    const tiers = buildTierList();

    container.innerHTML = `
        <div class="swatch-controls">
          <div class="swatch-controls__search-wrap">
            <input
              class="swatch-controls__search"
              type="search"
              placeholder="Search by fabric name or code"
              value="${escapeHtml(state.search)}"
              data-swatch-search
            >
          </div>
            ${state.search
        ? `<button type="button" class="swatch-controls__clear" data-swatch-clear-search aria-label="Clear search">×</button>`
        : ""
      }
          </div>
          <div class="swatch-filters">
            ${tiers
        .map((tier) => {
          const active = state.activeTier === tier.value ? "is-active" : "";
          const count =
            tier.value === "all"
              ? tierCounts.all || 0
              : tierCounts[tier.value] || 0;
          return `
                  <button
                    type="button"
                    class="swatch-filter ${active}"
                    data-swatch-tier="${escapeHtml(tier.value)}"
                  >
                    ${escapeHtml(tier.label)} (${count})
                  </button>
                `;
        })
        .join("")}
          </div>
          ${renderTierGuide()}
        </div>
      `;
  }

  function renderGrid() {
    const container = root.querySelector(".swatch-app__grid-wrap");

    if (!state.filteredSwatches.length) {
      container.innerHTML = `<div class="swatch-app__empty">No swatches match your current filter.</div>`;
      return;
    }

    const items = state.filteredSwatches.slice(0, state.visibleCount);

    container.innerHTML = `
      <div class="swatch-grid">
        ${items
        .map((swatch) => {
          const inCart = state.cartSwatchIds.has(swatch.id);
          const isSelected = state.selected.has(swatch.id);
          const isPreview = state.previewSwatchId === swatch.id;
          const isRemoving = state.removingSwatchIds.has(swatch.id);

          const classes = [
            "swatch-card",
            isSelected ? "is-selected" : "",
            isPreview ? "is-preview" : "",
            inCart ? "is-in-cart" : "",
            isRemoving ? "is-removing" : "",
          ]
            .filter(Boolean)
            .join(" ");

          const statusHtml = isRemoving
            ? `<div class="swatch-card__status">Removing...</div>`
            : inCart
              ? `<div class="swatch-card__status">In Cart</div>`
              : `<div class="swatch-card__status swatch-card__status--empty">&nbsp;</div>`;

          return `
              <article class="${classes}" data-swatch-card="${escapeHtml(swatch.id)}">
                <div class="swatch-card__image-wrap">
                  <img
                    class="swatch-card__image"
                    src="${escapeHtml(buildBcpoPreviewUrl(swatch.image_url, 500))}"
                    alt="${escapeHtml(swatch.web_name)}"
                    loading="lazy"
                    decoding="async"
                  >
                  <button
                    type="button"
                    class="swatch-card__view"
                    data-swatch-view="${escapeHtml(swatch.id)}"
                    aria-label="View larger image"
                  >
                    ⤢
                  </button>
                </div>
                
                ${statusHtml}
                <div class="swatch-card__code">${escapeHtml(swatch.fabric_code)}</div>
                <div class="swatch-card__name">${escapeHtml(swatch.web_name)}</div>
                ${swatch.openness
              ? `<div class="swatch-card__openness-badge">${escapeHtml(swatch.openness)}</div>`
              : ""
            }
                <div class="swatch-card__collection">
                  ${escapeHtml(swatch.collection_label || swatch.tier || "")}
                </div>
                <div class="swatch-card__footer">
                  <button
                    type="button"
                    class="swatch-card__select"
                    data-swatch-toggle="${escapeHtml(swatch.id)}"
                    ${isRemoving ? "disabled" : ""}
                  >
                    ${isRemoving
              ? "Removing..."
              : inCart
                ? "Remove"
                : isSelected
                  ? "Selected"
                  : "Select"
            }
                  </button>
                </div>
              </article>
            `;
        })
        .join("")}
      </div>
      ${state.filteredSwatches.length > state.visibleCount
        ? `
            <div class="swatch-browser__actions">
              <button type="button" class="swatch-load-more" data-swatch-load-more>
                Load More
              </button>
            </div>
          `
        : ""
      }
    `;

  }

  function renderSelected() {
    const container = root.querySelector(".swatch-app__selected-wrap");
    const selected = getSelectedArray();
    const total = selected.length;
    const totalPrice = selected.reduce(
      (sum, item) => sum + Number(item.price || 2),
      0,
    );

    container.innerHTML = `
      <aside class="swatch-selected ${state.isAdding ? "is-adding" : ""}">
        <h2 class="swatch-selected__title">Selected Swatches</h2>
        <div class="swatch-selected__count">${total} selected on this page</div>

        <div class="swatch-selected__list">
          ${selected.length
        ? selected
          .map(
            (swatch) => `
                      <div class="swatch-selected-item">
                        <img class="swatch-selected-item__thumb" src="${escapeHtml(swatch.image_url)}" alt="${escapeHtml(swatch.web_name)}">
                        <div>
                          <div class="swatch-selected-item__code">${escapeHtml(swatch.fabric_code)}</div>
                          <div class="swatch-selected-item__name">${escapeHtml(swatch.web_name)}</div>
                        </div>
                        <button type="button" class="swatch-selected-item__remove" data-swatch-remove="${escapeHtml(swatch.id)}">
                          Remove
                        </button>
                      </div>
                    `,
          )
          .join("")
        : `<div class="swatch-app__empty">No swatches selected yet.</div>`
      }
        </div>

        <div class="swatch-selected__summary">
          <div><strong>Total:</strong> ${formatMoney(totalPrice)}</div>
          <div><strong>Price:</strong> ${formatMoney(state.data?.swatch_price || 2)} each</div>
        </div>

        <button
          type="button"
          class="swatch-selected__add"
          data-swatch-add
          ${selected.length === 0 || state.isAdding ? "disabled" : ""}
        >
          ${state.isAdding ? "Adding..." : `Add ${selected.length || ""} Swatches to Cart`}
        </button>

        <div class="swatch-selected__note">
          Flat $5 shipping applies to swatch orders. Ships within 1 week.
        </div>
      </aside>
    `;
  }

  function updateSearchClearButton() {
    const wrap = root.querySelector(".swatch-controls__search-wrap");
    if (!wrap) return;

    let clearBtn = wrap.querySelector("[data-swatch-clear-search]");

    if (state.search) {
      if (!clearBtn) {
        clearBtn = document.createElement("button");
        clearBtn.type = "button";
        clearBtn.className = "swatch-controls__clear";
        clearBtn.setAttribute("data-swatch-clear-search", "");
        clearBtn.setAttribute("aria-label", "Clear search");
        clearBtn.textContent = "×";
        wrap.appendChild(clearBtn);
      }
    } else if (clearBtn) {
      clearBtn.remove();
    }
  }

  function bindEvents() {
    window.addEventListener("scroll", updateScrollTopVisibility);

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && state.isMobileModalOpen) {
        closeSwatchModal();
      }
    });

    document.addEventListener("click", (event) => {
      const headerCartTrigger = event.target.closest("#HeaderCartTrigger");
      const drawerClose = event.target.closest("header-drawer[close='cart:close'], .site-header__drawer .js-drawer-close, .site-header__drawer .drawer__close");
      if (headerCartTrigger || drawerClose) {
        setTimeout(() => {
          renderMobileCartButton();
        }, 150);
      }
    });

    root.addEventListener("click", (event) => {
      const scrollTopBtn = event.target.closest("[data-swatch-scroll-top]");
      if (scrollTopBtn) {
        scrollToSwatchTop();
        return;
      }
    });

    root.addEventListener("input", (event) => {
      const input = event.target.closest("[data-swatch-search]");
      if (!input) return;

      state.search = input.value || "";
      resetVisibleCount();
      applyFilters();
      updateSearchClearButton();
      renderGrid();
      renderPreview();
      requestDesktopStickyUpdate();
      renderMobileSelectedBar();
      renderMobileCartButton();
    });

    root.addEventListener("click", (event) => {
      const tierBtn = event.target.closest("[data-swatch-tier]");
      if (tierBtn) {
        state.activeTier = tierBtn.dataset.swatchTier;
        resetVisibleCount();
        applyFilters();
        renderControls();
        renderGrid();
        renderPreview();
        requestDesktopStickyUpdate();
        return;
      }

      const clearSearchBtn = event.target.closest("[data-swatch-clear-search]");
      if (clearSearchBtn) {
        state.search = "";
        resetVisibleCount();
        applyFilters();
        updateSearchClearButton();
        renderGrid();
        renderPreview();

        const input = root.querySelector("[data-swatch-search]");
        if (input) {
          input.value = "";
          input.focus();
        }
        return;
      }

      const openCartBtn = event.target.closest("[data-swatch-open-cart]");
      if (openCartBtn) {
        const headerCartTrigger = document.getElementById("HeaderCartTrigger");
        if (headerCartTrigger) {
          headerCartTrigger.click();
        }
        return;
      }

      const reviewSelectedBtn = event.target.closest("[data-swatch-review-selected]");
      if (reviewSelectedBtn) {
        scrollToSelectedPanel();
        return;
      }

      const modalOpenBtn = event.target.closest("[data-swatch-view]");
      if (modalOpenBtn) {
        event.stopPropagation();
        openSwatchModal(modalOpenBtn.dataset.swatchView);
        return;
      }

      const modalCloseBtn = event.target.closest("[data-swatch-modal-close]");
      if (modalCloseBtn) {
        closeSwatchModal();
        return;
      }

      const toggleBtn = event.target.closest("[data-swatch-toggle]");
      if (toggleBtn) {
        const swatchId = toggleBtn.dataset.swatchToggle;

        if (state.cartSwatchIds.has(swatchId)) {
          removeSwatchFromCart(swatchId);
        } else {
          toggleSwatch(swatchId);
        }
        return;
      }

      const removeBtn = event.target.closest("[data-swatch-remove]");
      if (removeBtn) {
        removeSelected(removeBtn.dataset.swatchRemove);
        return;
      }

      const loadMoreBtn = event.target.closest("[data-swatch-load-more]");
      if (loadMoreBtn) {
        loadMore();
        requestDesktopStickyUpdate();
        return;
      }

      const addBtn = event.target.closest("[data-swatch-add]");
      if (addBtn) {
        addSelectedToCart();
        return;
      }

      const card = event.target.closest("[data-swatch-card]");
      if (card) {
        const swatchId = card.dataset.swatchCard;

        if (state.cartSwatchIds.has(swatchId)) {
          setPreview(swatchId);
        } else {
          toggleSwatch(swatchId);
        }
      }
    });

    root.addEventListener("mouseover", (event) => {
      const card = event.target.closest("[data-swatch-card]");
      if (!card) return;
      setPreview(card.dataset.swatchCard);
    });

    document.addEventListener("cart:open", () => {
      setTimeout(() => {
        setupCartDrawerObserver();
        scheduleCartSync(150);
      }, 50);
    });
  }

  function renderAll() {
    renderShell();
    renderHeader();
    renderControls();
    renderPreview();
    renderGrid();
    renderSelected();
    renderModal();
    renderMobileSelectedBar();
    renderMobileCartButton();
    updateScrollTopVisibility();
    requestDesktopStickyUpdate();
  }

  async function init() {
    if (!config.jsonFile) {
      root.innerHTML = `<div class="swatch-app__error">Missing swatch data file metafield.</div>`;
      return;
    }

    try {
      const productKey = String(config.jsonFile).replace("-data.js", "").trim();

      const data = window.HS_SWATCH_DATA && window.HS_SWATCH_DATA[productKey];

      if (!data) {
        throw new Error(`Swatch data not found for key: ${productKey}`);
      }

      state.data = data;
      state.allSwatches = (Array.isArray(data.swatches) ? data.swatches : []).map((swatch) => {
        const code = String(swatch.fabric_code || "").toLowerCase();
        const name = String(swatch.web_name || "").toLowerCase();
        const searchText = String(swatch.search_text || "").toLowerCase();
        const collection = String(swatch.collection_label || "").toLowerCase();
        const tier = String(swatch.tier || "").toLowerCase();
        const openness = String(swatch.openness || "").toLowerCase();

        return {
          ...swatch,
          _code: code,
          _codeNormalized: code.replace(/[\s-]+/g, ""),
          _codeSuffix: code.split("-").pop() || "",
          _searchBlob: [searchText, name, code, collection, tier, openness].join(" ")
        };
      });

      applyFilters();

      if (state.filteredSwatches.length) {
        state.previewSwatchId = state.filteredSwatches[0].id;
      }

      renderAll();
      bindEvents();
      setupCartDrawerVisibilityObserver();
      setupCartDrawerObserver();
      setupDesktopStickyFallback();

      syncSwatchAppFromCart();
    } catch (error) {
      console.error("Swatch init error:", error);
      root.innerHTML = `<div class="swatch-app__error">Unable to load swatches right now.<br><small>${escapeHtml(error.message)}</small></div>`;
    }
  }

  init();
})();
